/**
 * Calculates the angle between three 3D points (e.g., shoulder, elbow, wrist).
 * Uses the law of cosines or vector dot product.
 * 
 * @param {object} a - First point {x, y, z}
 * @param {object} b - Second point (vertex) {x, y, z}
 * @param {object} c - Third point {x, y, z}
 * @returns {number} Angle in degrees (0-180)
 */
export function calculateJointAngle(a, b, c) {
  if (!a || !b || !c) return 0;
  
  const vectorBA = {
    x: a.x - b.x,
    y: a.y - b.y,
    z: a.z - b.z
  };
  
  const vectorBC = {
    x: c.x - b.x,
    y: c.y - b.y,
    z: c.z - b.z
  };
  
  const dotProduct = vectorBA.x * vectorBC.x + vectorBA.y * vectorBC.y + vectorBA.z * vectorBC.z;
  
  const magnitudeBA = Math.sqrt(vectorBA.x * vectorBA.x + vectorBA.y * vectorBA.y + vectorBA.z * vectorBA.z);
  const magnitudeBC = Math.sqrt(vectorBC.x * vectorBC.x + vectorBC.y * vectorBC.y + vectorBC.z * vectorBC.z);
  
  if (magnitudeBA === 0 || magnitudeBC === 0) return 0; // Avoid division by zero
  
  // Clamp the value to [-1, 1] to prevent floating point errors with acos
  const cosTheta = Math.max(-1, Math.min(1, dotProduct / (magnitudeBA * magnitudeBC)));
  
  const angle = Math.acos(cosTheta) * (180 / Math.PI);
  
  return angle;
}

/**
 * Calculates the Euclidean distance between two 3D points.
 * 
 * @param {object} point1 - First point {x, y, z}
 * @param {object} point2 - Second point {x, y, z}
 * @returns {number} Distance
 */
export function calculateDistance3D(point1, point2) {
   if (!point1 || !point2) return 0;
   
   const dx = point2.x - point1.x;
   const dy = point2.y - point1.y;
   const dz = point2.z - point1.z;
   return Math.sqrt(dx*dx + dy*dy + dz*dz);
} 